import React, { useCallback, useMemo, useState, useEffect, useRef } from 'react';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';

interface IndividualAadhaarInputProps {
  // Controlled pattern (used by JourneyPage)
  value?: { aadhaar_number?: string | null };
  onChange?: (val: { aadhaar_number: string }) => void;
  disabled?: boolean;
  registerSubmit?: (handler: () => Promise<boolean>) => void;

  // Legacy pattern (kept for backward compatibility)
  initialData?: { aadhaar_number?: string };
  onDataChange?: (data: { aadhaar_number: string }) => void;
}

function validateAadhaar(value: string): string {
  const trimmed = (value || '').trim();
  if (!trimmed) return 'Aadhaar number is required';
  if (!/^\d+$/.test(trimmed)) return 'Aadhaar number must contain digits only';
  if (trimmed.length !== 12) return 'Aadhaar number must be exactly 12 digits';
  return '';
}

const IndividualAadhaarInput: React.FC<IndividualAadhaarInputProps> = ({
  value,
  onChange,
  disabled,
  registerSubmit,
  initialData,
  onDataChange,
}) => {
  const aadhaarNumber = useMemo(() =>
    (value?.aadhaar_number ?? initialData?.aadhaar_number ?? '') || '',
    [value?.aadhaar_number, initialData?.aadhaar_number]
  );

  const [touched, setTouched] = useState(false);

  const valueRef = useRef(value);
  valueRef.current = value;

  useEffect(() => {
    if (!registerSubmit) return;
    registerSubmit(async () => {
      setTouched(true);
      const aadhaar = (valueRef.current?.aadhaar_number ?? '') || '';
      return validateAadhaar(aadhaar) === '';
    });
  }, [registerSubmit]);

  const handleChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value.replace(/\D/g, '').slice(0, 12);
    onChange?.({ aadhaar_number: val });
    onDataChange?.({ aadhaar_number: val });
  }, [onChange, onDataChange]);

  const error = touched ? validateAadhaar(aadhaarNumber) : '';

  return (
    <div className="space-y-1.5">
      <Label htmlFor="aadhaar_number" className="text-gray-500 font-medium">
        Aadhaar Number <span className="text-red-500">*</span>
      </Label>
      <Input
        id="aadhaar_number"
        type="text"
        value={aadhaarNumber}
        onChange={handleChange}
        onBlur={() => setTouched(true)}
        placeholder="Enter 12-digit Aadhaar number"
        maxLength={12}
        disabled={disabled}
        inputMode="numeric"
        className={`border-gray-200 focus:border-intellend-green focus:ring-intellend-green rounded-xl py-6 ${error ? 'border-red-400 focus:border-red-500' : ''}`}
      />
      {error && (
        <p className="text-xs text-red-500 mt-0.5">{error}</p>
      )}
    </div>
  );
};

export default IndividualAadhaarInput;
